import React, {useState, useEffect, useRef} from "react"
import _ from "lodash"
import moment from 'moment'
import Isvg from 'react-inlinesvg'

import comparisonService, { Comparison } from "data/comparison"
import folderService from "data/folder"

import CheckboxInput from "components/inputs/checkbox"
import DropdownInput from "components/inputs/dropdown"
import FolderNav from "components/folder/explorer/folder-nav"
import FormAutomation from "components/form-automation"
import { WithTip } from 'components/tooltip/with-tip'

import { CaretDownIcon } from "images/icons/tools/"
import { CaretRightIcon } from "images/icons/general"

import { ComparisonDetailView_s, Details_s } from "components/asset-management/comparison/detail/comparison-detail-styles"
import { InputText_s, DateText_s } from 'styles/input-styles'
import { Button_s, Label_s } from 'styles/app-styles'

type IComparisonDetail = {
  currentComparison: Comparison
}

const ComparisonDetailView = (props: IComparisonDetail) => {
  const [comparison, setComparison] = useState<Comparison>(_.cloneDeep(props.currentComparison))
  const [folders, setFolders] = useState<object[]>([])
  const [sourceFolderId, setSourceFolderId] = useState<any>(_.get(props, "currentComparison.sourcefolderid") || null)
  const [sourceFolderPath, setSourceFolderPath] = useState<string>("")
  const [comparisonFolderId, setComparisonFolderId] = useState<any>(_.get(props, "currentComparison.comparisonfolderid") || null)
  const [comparisonFolderPath, setComparisonFolderPath] = useState<string>("")
  const [beginDate, setBeginDate] = useState<string>(_.get(props, "currentComparison.comparisondetail.begindate") || '')
  // const [calendarYear, setCalendarYear] = useState<boolean>(_.has(props, "currentComparison.comparisondetail.calendaryear") ?
  //   _.get(props, "currentComparison.comparisondetail.calendaryear") : true)
  const [displayAccount, setDisplayAccount] = useState<boolean>(_.has(props, "currentComparison.comparisondetail.displayaccount") ?
    _.get(props, "currentComparison.comparisondetail.displayaccount") : true)
  const [lineItemDisplay, setLineItemDisplay] = useState<string>(_.get(props, "currentComparison.comparisondetail.lineitemdisplay") || 'Detail Account #')
  const [term, setTerm] = useState<number|null>(_.get(props, "currentComparison.comparisondetail.term") || null)
  const [sourceSearch, setSourceSearch] = useState<string>("")
  const [comparisonSearch, setComparisonSearch] = useState<string>("")
  const lineItemDetailOptions = [{label: 'Detail Account #', value: 'Detail Account #'},
                                 {label: 'Summary Account #', value: 'Summary Account #'}]

  const [showSourceNav, setShowSourceNav] = useState<boolean>(false)
  const [showComparisonNav, setShowComparisonNav] = useState<boolean>(false)

  const persist = (field, value) => {
    const newData = _.cloneDeep(comparison)
    if(!newData.comparisondetail) newData.comparisondetail = {}

    _.set(newData, field, value)
    setComparison(newData)
    comparisonService.patch(newData)
  }

  useEffect(() => {
    const newData = _.cloneDeep(comparison)
    const sourceFolder = _.find(folders, (f) => f.folderid === (sourceFolderId || _.get(newData, "sourcefolderid")))

    if(!newData.comparisondetail) newData.comparisondetail = {}

    if(sourceFolder && !newData.comparisondetail.begindate) {
      newData.comparisondetail.begindate = sourceFolder
        ? _.get(sourceFolder, "reportbegindate")
        : _.get(newData, "comparisondetail.begindate")
        ? _.get(newData, "comparisondetail.begindate")
        : moment(new Date).format("MM/YYYY")
      setBeginDate(newData.comparisondetail.begindate)
    }

    if(sourceFolder && !newData.comparisondetail.term) {
      newData.comparisondetail.term = sourceFolder
        ? _.get(sourceFolder, "term")
        : _.get(newData, "comparisondetail.term")
        ? _.get(newData, "comparisondetail.term")
        : 10
      setTerm(newData.comparisondetail.term)
    }

    if(!newData.comparisondetail.begindate || !newData.comparisondetail.term) setComparison(newData)
  }, [folders, sourceFolderId])

  useEffect(() => {
    folderService.fetchAllFolders()
      .then((folderRes) => {
        setFolders(folderRes)
        if(sourceFolderId) setSourceFolderPath(folderService.getCodePath(sourceFolderId).join(' > '))
        if(comparisonFolderId) setComparisonFolderPath(folderService.getCodePath(comparisonFolderId).join(' > '))
      })
  }, [])

  useEffect(() => {
    if(sourceFolderId) setSourceFolderPath(folderService.getCodePath(sourceFolderId).join(' > '))
    if(comparisonFolderId) setComparisonFolderPath(folderService.getCodePath(comparisonFolderId).join(' > '))
  }, [sourceFolderId, comparisonFolderId])

  return (
    <ComparisonDetailView_s id="comparison-detail-container">
      <div>
        <Details_s>
          <div className="input-group">
            <Label_s>Source Folder</Label_s>
            <WithTip position="bottom">
              <span>{sourceFolderPath}</span>
              <Button_s
                id="sourceFolder"
                className="selected-folder automated"
                key={comparison.comparisonid}
                onClick={() => setShowSourceNav(!showSourceNav)}
              >
                <span className="path">{sourceFolderPath}</span>
                <span className="icon">{showSourceNav ? <Isvg src={CaretDownIcon} className="icon" /> : <Isvg src={CaretRightIcon} className="icon" />}</span>
              </Button_s>
            </WithTip>
          </div>
          <div className="input-group">
            <Label_s>Start Date</Label_s>
            <DateText_s
              className="small-input automated"
              value={beginDate}
              persist={(val) => {
                setBeginDate(val)
                persist("comparisondetail.begindate", val)
              }}
              withoutDays
              key={comparison.comparisonid}
            />
          </div>
          <div className="input-group">
            <Label_s>Term</Label_s>
            <InputText_s
              className="small-input automated"
              onChange={(e) => setTerm(e.target.value)}
              onBlur={() => persist("comparisondetail.term", term)}
              value={term}
              key={comparison.comparisonid}
            />
          </div>
          {/* <div className="input-group">
            <Label_s>Calendar Year?</Label_s>
            <CheckboxInput
              field="calendaryear"
              value={calendarYear}
              persist={(field:string, val:boolean) => {
                setCalendarYear(val)
                persist("comparisondetail.calendaryear", val)
              }}
              key={comparison.comparisonid}
            />
          </div> */}
          <div className="input-group">
            <Label_s>Line Item Display</Label_s>
            <DropdownInput
              className="line-item-detail-input automated"
              persist={(field:string, val:string)=> {
                setLineItemDisplay(val)
                persist("comparisondetail.lineitemdisplay", val)
              }}
              field="lineItemDisplay"
              options={lineItemDetailOptions}
              value={lineItemDisplay}
              key={comparison.comparisonid}
            />
          </div>
          <div className="input-group">
            <Label_s>Display Account?</Label_s>
            <CheckboxInput
              className="automated"
              field="displayaccount"
              value={displayAccount}
              persist={(field:string, val:boolean) => {
                setDisplayAccount(val)
                persist("comparisondetail.displayaccount", val)
              }}
              key={comparison.comparisonid}
            />
          </div>
        </Details_s>
        {showSourceNav && <Details_s>
          <InputText_s
            className="search automated"
            onChange={(e) => setSourceSearch(e.target.value)}
            value={sourceSearch}
            placeholder="Search"
          />
        </Details_s>}
        {showSourceNav && <Details_s>
          <FolderNav selectedFolder={sourceFolderId} assetsOnly={true} minWidth={468} minHeight={300} maxHeight={500} onSelectFolder={(fid) => {
            setSourceFolderId(fid)
            persist("sourcefolderid", fid)
          }} searchTerm={sourceSearch} key={comparison.comparisonid}/>
        </Details_s>}
      </div>
      <div>
        <Details_s>
          <div className="input-group">
            <Label_s>Comparison Folder</Label_s>
            <WithTip position="bottom">
              <span>{comparisonFolderPath}</span>
              <Button_s
                id="comparisonFolder"
                className="selected-folder automated"
                key={comparison.comparisonid}
                onClick={() => setShowComparisonNav(!showComparisonNav)}
              >
                <span className="path">{comparisonFolderPath}</span>
                <span className="icon">{showComparisonNav ? <Isvg src={CaretDownIcon} className="icon" /> : <Isvg src={CaretRightIcon} className="icon" />}</span>
              </Button_s>
            </WithTip>
          </div>
        </Details_s>
        {showComparisonNav && <Details_s>
          <InputText_s
            className="search automated"
            onChange={(e) => setComparisonSearch(e.target.value)}
            value={comparisonSearch}
            placeholder="Search"
          />
        </Details_s>}
        {showComparisonNav && <Details_s>
          <FolderNav selectedFolder={comparisonFolderId} assetsOnly={true} minWidth={468} minHeight={300} maxHeight={500} onSelectFolder={(fid) => {
            setComparisonFolderId(fid)
            persist("comparisonfolderid", fid)
          }} searchTerm={comparisonSearch} key={comparison.comparisonid}/>
        </Details_s>}
      </div>
      <FormAutomation automationContainerId="comparison-detail-container"/>
    </ComparisonDetailView_s>
  )
}

export default ComparisonDetailView
